import { apiClient } from '@/lib/api/client'

export interface SalesWindow { orders: number; units: number; revenue: number; commission: number }
export interface SalesDay extends SalesWindow { day: string }

export interface TrafficCounts { sessions: number; pageviews: number; visitors?: number }
export type TrafficByProperty = Record<string, TrafficCounts>

/** Revenue (USD) and traffic from the nightly analytics warehouse (lib/analytics-warehouse). */
export interface Growth {
  currency: 'USD'
  /** ISO codes left out of every traffic figure, and the same in words ("Asia"). */
  excludedCountries: string[]
  excludedLabel: string
  channels: { key: string; label: string }[]
  properties: { key: string; label: string }[]
  sources: Record<'shopify' | 'posthog', { lastSuccess: string | null; lastError: string | null }>
  /** 365 complete days, oldest first, ending yesterday (Central). `excluded` is sessions left out. */
  daily: {
    day: string; orders: number; units: number; revenue: number; storeRevenue: number
    channels: Record<string, { revenue: number; orders: number }>
    traffic: TrafficByProperty; excluded: number; automated: number
  }[]
  /** Every month on record, oldest first; the last is the month to date. */
  monthly: { month: string; orders: number; units: number; revenue: number; storeRevenue: number; traffic: TrafficByProperty; excluded: number; automated: number }[]
  /** Top included countries by sessions, last 30 days. */
  countries: { country: string; sessions: number; pageviews: number }[]
}

/** GET /api/admin/dashboard (lib/admin-dashboard). A section that failed to read is null. */
export interface DashboardReport {
  generatedAt: string
  timezone: string
  errors: string[]
  growth: Growth | null
  sales: {
    /** USD once the analytics batch has recorded an exchange rate; GBP (the shop currency) before. */
    currency: 'USD' | 'GBP'
    lines: number
    daily: SalesDay[]
    current: SalesWindow | null
    previous: SalesWindow | null
    topArtists: { id: number; name: string; revenue: number; units: number }[]
    topArtworks: { id: number; name: string; artist: string; revenue: number; units: number }[]
  } | null
  catalogue: {
    total: number
    imported: number
    byStatus: Record<string, { total: number; imported: number }>
    daily: { day: string; uploads: number; imports: number }[]
  } | null
  publishing: {
    daily: { day: string; completed: number; failed: number }[]
    queue: { pending: number; running: number }
    last24h: { completed: number; failed: number; recovered: number; causes: { pattern: string; count: number; artworkIds: number[] }[] }
  } | null
  support: {
    open: number
    byStatus: Record<string, number>
    urgent: number
    daily: { day: string; created: number; resolved: number }[]
    medianFirstResponseMinutes: number | null
  } | null
  approvals: {
    pending: number
    waiting: number
    held: number
    oldest: { id: number; name: string; artist: string; held: boolean }[]
  } | null
}

export async function getDashboard(): Promise<DashboardReport> {
  const { data } = await apiClient.get<DashboardReport>('/api/admin/dashboard')
  return data
}
